import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { migrateCompanyMembers } from '../utils/migrateCompanyMembers';

const DatabaseInspector: React.FC = () => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState<any>(null);

  const runMigration = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const result = await migrateCompanyMembers();
      setMigrationResult(result);
      if (result.success) {
        // Refresh the data after migration
        await inspectDatabase();
      }
    } catch (error) {
      console.error('Migration error:', error);
      setMigrationResult({ success: false, error });
    } finally {
      setMigrating(false);
    }
  };

  const inspectDatabase = async () => {
    setLoading(true);
    try {
      // Get all companies
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompanies(companiesData);
      console.log('🔍 ALL COMPANIES:', companiesData);

      // Get all company members
      const membersSnapshot = await getDocs(collection(db, 'companyMembers'));
      const membersData = membersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMembers(membersData);
      console.log('🔍 ALL COMPANY MEMBERS:', membersData);

      // Check each company's members
      for (const company of companiesData) {
        const companyMembersQuery = query(
          collection(db, 'companyMembers'),
          where('companyId', '==', company.id)
        );
        const companyMembersSnapshot = await getDocs(companyMembersQuery);
        const companyMembers = companyMembersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`🔍 COMPANY "${(company as any).name}" (${company.id}) MEMBERS:`, companyMembers);
      }

    } catch (error) {
      console.error('❌ Error inspecting database:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    inspectDatabase();
  }, []);

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>🔍 Database Inspector</h2>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={inspectDatabase} disabled={loading}>
          {loading ? 'Inspecting...' : 'Refresh Inspection'}
        </button>
        <button 
          onClick={runMigration} 
          disabled={migrating}
          style={{ marginLeft: '10px', backgroundColor: '#ff6b6b', color: 'white' }}
        >
          {migrating ? 'Migrating...' : '🔧 Fix Missing joinedAt Fields'}
        </button>
      </div>

      {migrationResult && (
        <div style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: migrationResult.success ? '#d4edda' : '#f8d7da',
          border: `1px solid ${migrationResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          borderRadius: '5px'
        }}>
          <h3>{migrationResult.success ? '✅ Migration Successful!' : '❌ Migration Failed!'}</h3>
          {migrationResult.success ? (
            <div>
              <p>Updated: {migrationResult.updated} members</p>
              <p>Skipped: {migrationResult.skipped} members</p>
              <p>Total: {migrationResult.total} members</p>
            </div>
          ) : (
            <p>Error: {migrationResult.error?.message || 'Unknown error'}</p>
          )}
        </div>
      )}

      <div style={{ marginTop: '20px' }}>
        <h3>Companies ({companies.length})</h3>
        {companies.map(company => (
          <div key={company.id} style={{ margin: '10px 0', padding: '10px', border: '1px solid #ccc' }}>
            <strong>{(company as any).name}</strong> (ID: {company.id})
            <br />
            Created: {company.createdAt ? 
              (company.createdAt.toDate ? 
                company.createdAt.toDate().toLocaleDateString() : 
                new Date(company.createdAt).toLocaleDateString()
              ) : 'Unknown'}
          </div>
        ))}
      </div>

      <div style={{ marginTop: '20px' }}>
        <h3>Company Members ({members.length})</h3>
        {members.map(member => (
          <div key={member.id} style={{ margin: '10px 0', padding: '10px', border: '1px solid #ccc' }}>
            <strong>User ID:</strong> {member.userId}
            <br />
            <strong>Company ID:</strong> {member.companyId}
            <br />
            <strong>Role:</strong> {member.role}
            <br />
            <strong>Status:</strong> {member.status}
            <br />
            <strong>Invited At:</strong> {member.invitedAt}
            <br />
            <strong>Joined At:</strong> {member.joinedAt || 'MISSING!'}
          </div>
        ))}
      </div>

      <div style={{ marginTop: '20px', padding: '10px', background: '#f0f0f0' }}>
        <h3>📋 Summary</h3>
        <p>Total Companies: {companies.length}</p>
        <p>Total Company Members: {members.length}</p>
        <p>Members with joinedAt: {members.filter(m => m.joinedAt).length}</p>
        <p>Members without joinedAt: {members.filter(m => !m.joinedAt).length}</p>
      </div>
    </div>
  );
};

export default DatabaseInspector;
